/**
 * Content Cache Utility
 * 
 * Manages caching of study guides, questions, and images for offline access.
 * Uses IndexedDB for larger content and localStorage for metadata.
 * 
 * @module contentCache
 */

import { logger } from './logger';
import { storage, STORAGE_KEYS } from './storage';

/**
 * Content types that can be cached
 */
export const CONTENT_TYPES = {
  STUDY_GUIDE: 'study_guide',
  QUESTIONS: 'questions',
  IMAGE: 'image',
  SPELLING_WORDS: 'spelling_words',
  PARENT_REFERENCE: 'parent_reference'
};

/**
 * Cache configuration
 */
const CACHE_CONFIG = {
  MAX_SIZE: 50 * 1024 * 1024, // 50MB max cache size
  IMAGE_TTL: 7 * 24 * 60 * 60 * 1000, // 7 days for images
  CONTENT_TTL: 30 * 24 * 60 * 60 * 1000, // 30 days for content
  MAX_ITEMS: 1000 // Max number of cached items
};

/**
 * Content Cache Manager
 */
class ContentCacheManager {
  constructor() {
    this.cache = null;
    this.dbName = 'emmy-content-cache';
    this.dbVersion = 1;
    this.initPromise = null;
  }

  /**
   * Initialize IndexedDB for caching
   * @returns {Promise<void>}
   */
  async init() {
    if (this.initPromise) {
      return this.initPromise;
    }

    if (!('indexedDB' in window)) {
      logger.warn('IndexedDB not supported, falling back to localStorage');
      return;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        logger.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.cache = request.result;
        logger.info('Content cache initialized');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object stores for different content types
        if (!db.objectStoreNames.contains('content')) {
          const contentStore = db.createObjectStore('content', { keyPath: 'key' });
          contentStore.createIndex('type', 'type', { unique: false });
          contentStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('images')) {
          const imageStore = db.createObjectStore('images', { keyPath: 'url' });
          imageStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Cache content for offline access
   * 
   * @param {string} key - Unique key for the content
   * @param {Object} data - Content data to cache
   * @param {string} type - Content type (from CONTENT_TYPES)
   * @returns {Promise<boolean>} Success status
   */
  async cacheContent(key, data, type = CONTENT_TYPES.STUDY_GUIDE) {
    try {
      await this.init();

      const cacheItem = {
        key,
        type,
        data,
        timestamp: Date.now(),
        size: this.calculateSize(data)
      };

      if (this.cache) {
        const transaction = this.cache.transaction(['content'], 'readwrite');
        const store = transaction.objectStore('content');
        await store.put(cacheItem);
        
        // Update metadata in localStorage
        this.updateCacheMetadata(key, type, cacheItem.size);
        
        logger.debug(`Cached content: ${key} (${type})`);
        return true;
      } else {
        // Fallback to localStorage
        const cacheKey = `${STORAGE_KEYS.CACHED_CONTENT}:${key}`;
        storage.set(cacheKey, cacheItem);
        return true;
      }
    } catch (error) {
      logger.error('Failed to cache content:', error);
      return false;
    }
  }

  /**
   * Retrieve cached content with improved TTL handling
   * 
   * @param {string} key - Content key
   * @param {string} type - Content type (for TTL determination)
   * @returns {Promise<Object|null>} Cached content or null if not found/expired
   */
  async getCachedContent(key) {
    try {
      await this.init();

      if (this.cache) {
        const transaction = this.cache.transaction(['content'], 'readonly');
        const store = transaction.objectStore('content');
        const request = store.get(key);

        return new Promise((resolve) => {
          request.onsuccess = () => {
            const item = request.result;
            if (!item) {
              resolve(null);
              return;
            }

            // Check if expired - use specific TTL based on content type
            let ttl = CACHE_CONFIG.CONTENT_TTL;
            if (item.type === CONTENT_TYPES.IMAGE) {
              ttl = CACHE_CONFIG.IMAGE_TTL;
            } else if (item.type === CONTENT_TYPES.STUDY_GUIDE) {
              ttl = CACHE_CONFIG.STUDY_GUIDE_TTL;
            } else if (item.type === CONTENT_TYPES.QUESTIONS) {
              ttl = CACHE_CONFIG.QUESTIONS_TTL;
            }
            
            if (Date.now() - item.timestamp > ttl) {
              // Remove expired item
              this.removeCachedContent(key);
              resolve(null);
              return;
            }

            resolve(item.data);
          };

          request.onerror = () => {
            resolve(null);
          };
        });
      } else {
        // Fallback to localStorage
        const cacheKey = `${STORAGE_KEYS.CACHED_CONTENT}:${key}`;
        const item = storage.get(cacheKey);
        if (!item) return null;

        // Check expiration - use specific TTL based on content type
        let ttl = CACHE_CONFIG.CONTENT_TTL;
        if (item.type === CONTENT_TYPES.IMAGE) {
          ttl = CACHE_CONFIG.IMAGE_TTL;
        } else if (item.type === CONTENT_TYPES.STUDY_GUIDE) {
          ttl = CACHE_CONFIG.STUDY_GUIDE_TTL;
        } else if (item.type === CONTENT_TYPES.QUESTIONS) {
          ttl = CACHE_CONFIG.QUESTIONS_TTL;
        }
        
        if (Date.now() - item.timestamp > ttl) {
          storage.remove(cacheKey);
          return null;
        }

        return item.data;
      }
    } catch (error) {
      logger.error('Failed to get cached content:', error);
      return null;
    }
  }

  /**
   * Cache an image for offline access
   * 
   * @param {string} url - Image URL
   * @returns {Promise<boolean>} Success status
   */
  async cacheImage(url) {
    try {
      await this.init();

      // Check if already cached
      const existing = await this.getCachedImage(url);
      if (existing) {
        return true;
      }

      // Fetch and convert to blob
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }

      const blob = await response.blob();

      if (this.cache) {
        const transaction = this.cache.transaction(['images'], 'readwrite');
        const store = transaction.objectStore('images');
        
        const imageItem = {
          url,
          blob,
          timestamp: Date.now(),
          size: blob.size
        };

        await store.put(imageItem);
        logger.debug(`Cached image: ${url}`);
        return true;
      } else {
        // Fallback: convert to base64 and store in localStorage
        const reader = new FileReader();
        return new Promise((resolve, reject) => {
          reader.onloadend = () => {
            const base64 = reader.result;
            const cacheKey = `${STORAGE_KEYS.CACHED_CONTENT}:image:${url}`;
            storage.set(cacheKey, {
              url,
              data: base64,
              timestamp: Date.now(),
              type: CONTENT_TYPES.IMAGE
            });
            resolve(true);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
    } catch (error) {
      logger.error('Failed to cache image:', error);
      return false;
    }
  }

  /**
   * Get cached image
   * 
   * @param {string} url - Image URL
   * @returns {Promise<string|null>} Image data URL or null if not cached/expired
   */
  async getCachedImage(url) {
    try {
      await this.init();

      if (this.cache) {
        const transaction = this.cache.transaction(['images'], 'readonly');
        const store = transaction.objectStore('images');
        const request = store.get(url);

        return new Promise((resolve) => {
          request.onsuccess = () => {
            const item = request.result;
            if (!item) {
              resolve(null);
              return;
            }

            // Check if expired
            if (Date.now() - item.timestamp > CACHE_CONFIG.IMAGE_TTL) {
              this.removeCachedImage(url);
              resolve(null);
              return;
            }

            // Convert blob to data URL
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(item.blob);
          };

          request.onerror = () => resolve(null);
        });
      } else {
        // Fallback to localStorage
        const cacheKey = `${STORAGE_KEYS.CACHED_CONTENT}:image:${url}`;
        const item = storage.get(cacheKey);
        if (!item) return null;

        if (Date.now() - item.timestamp > CACHE_CONFIG.IMAGE_TTL) {
          storage.remove(cacheKey);
          return null;
        }

        return item.data;
      }
    } catch (error) {
      logger.error('Failed to get cached image:', error);
      return null;
    }
  }

  /**
   * Remove cached content
   * 
   * @param {string} key - Content key
   */
  async removeCachedContent(key) {
    try {
      await this.init();

      if (this.cache) {
        const transaction = this.cache.transaction(['content'], 'readwrite');
        const store = transaction.objectStore('content');
        await store.delete(key);
      } else {
        const cacheKey = `${STORAGE_KEYS.CACHED_CONTENT}:${key}`;
        storage.remove(cacheKey);
      }

      this.removeCacheMetadata(key);
    } catch (error) {
      logger.error('Failed to remove cached content:', error);
    }
  }

  /**
   * Remove cached image
   * 
   * @param {string} url - Image URL
   */
  async removeCachedImage(url) {
    try {
      await this.init();

      if (this.cache) {
        const transaction = this.cache.transaction(['images'], 'readwrite');
        const store = transaction.objectStore('images');
        await store.delete(url);
      } else {
        const cacheKey = `${STORAGE_KEYS.CACHED_CONTENT}:image:${url}`;
        storage.remove(cacheKey);
      }
    } catch (error) {
      logger.error('Failed to remove cached image:', error);
    }
  }

  /**
   * Clear all cached content
   */
  async clearCache() {
    try {
      await this.init();

      if (this.cache) {
        const transaction = this.cache.transaction(['content', 'images'], 'readwrite');
        await Promise.all([
          transaction.objectStore('content').clear(),
          transaction.objectStore('images').clear()
        ]);
      }

      // Clear localStorage cache
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(STORAGE_KEYS.CACHED_CONTENT)) {
          localStorage.removeItem(key);
        }
      });

      // Clear metadata
      storage.remove(`${STORAGE_KEYS.CACHED_CONTENT}:metadata`);

      logger.info('Content cache cleared');
    } catch (error) {
      logger.error('Failed to clear cache:', error);
    }
  }

  /**
   * Get cache statistics
   * 
   * @returns {Promise<Object>} Cache stats
   */
  async getCacheStats() {
    try {
      await this.init();

      const metadata = storage.get(`${STORAGE_KEYS.CACHED_CONTENT}:metadata`) || {};
      
      if (this.cache) {
        const transaction = this.cache.transaction(['content', 'images'], 'readonly');
        
        const contentCount = await new Promise((resolve) => {
          const store = transaction.objectStore('content');
          const request = store.count();
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => resolve(0);
        });

        const imageCount = await new Promise((resolve) => {
          const store = transaction.objectStore('images');
          const request = store.count();
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => resolve(0);
        });

        return {
          totalItems: contentCount + imageCount,
          contentItems: contentCount,
          imageItems: imageCount,
          totalSize: metadata.totalSize || 0,
          lastCleared: metadata.lastCleared || null
        };
      } else {
        // Calculate from localStorage
        const keys = Object.keys(localStorage);
        const cacheKeys = keys.filter(key => key.startsWith(STORAGE_KEYS.CACHED_CONTENT));
        
        return {
          totalItems: cacheKeys.length,
          contentItems: cacheKeys.filter(k => !k.includes(':image:')).length,
          imageItems: cacheKeys.filter(k => k.includes(':image:')).length,
          totalSize: metadata.totalSize || 0,
          lastCleared: metadata.lastCleared || null
        };
      }
    } catch (error) {
      logger.error('Failed to get cache stats:', error);
      return {
        totalItems: 0,
        contentItems: 0,
        imageItems: 0,
        totalSize: 0,
        lastCleared: null
      };
    }
  }

  /**
   * Calculate size of data in bytes
   * 
   * @param {*} data - Data to calculate size for
   * @returns {number} Size in bytes
   */
  calculateSize(data) {
    try {
      const str = JSON.stringify(data);
      return new Blob([str]).size;
    } catch {
      return 0;
    }
  }

  /**
   * Update cache metadata
   * 
   * @param {string} key - Content key
   * @param {string} type - Content type
   * @param {number} size - Size in bytes
   */
  updateCacheMetadata(key, type, size) {
    const metadata = storage.get(`${STORAGE_KEYS.CACHED_CONTENT}:metadata`) || {
      items: {},
      totalSize: 0,
      lastCleared: null
    };

    if (!metadata.items[key]) {
      metadata.items[key] = { type, size };
      metadata.totalSize += size;
    } else {
      // Update existing
      metadata.totalSize -= metadata.items[key].size;
      metadata.items[key] = { type, size };
      metadata.totalSize += size;
    }

    storage.set(`${STORAGE_KEYS.CACHED_CONTENT}:metadata`, metadata);
  }

  /**
   * Remove from cache metadata
   * 
   * @param {string} key - Content key
   */
  removeCacheMetadata(key) {
    const metadata = storage.get(`${STORAGE_KEYS.CACHED_CONTENT}:metadata`) || {
      items: {},
      totalSize: 0
    };

    if (metadata.items[key]) {
      metadata.totalSize -= metadata.items[key].size;
      delete metadata.items[key];
      storage.set(`${STORAGE_KEYS.CACHED_CONTENT}:metadata`, metadata);
    }
  }
}

// Export singleton instance
export const contentCache = new ContentCacheManager();

